import React, { useState } from 'react';
import { useUserContext } from '../../store/user_context';

const AddRestaurantMenu = () => {
  const { user } = useUserContext();
  const [formData, setFormData] = useState({
    menuName: '',
    description: '',
    telephone: '',
    address: '',
    vatValue: '',
    deliveryPrice: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const apiUrl = import.meta.env.VITE_API_URL;
  const createMenuEndpoint = import.meta.env.VITE_add_menu;
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateCSVFile = async (file) => {
    try {
      // Use the same parsing logic to validate
      await parseCSVFile(file);
      return { valid: true };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is CSV
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        // Validate CSV content
        const validation = await validateCSVFile(file);
        if (validation.valid) {
          setSelectedFile(file);
          setValidationErrors(prev => ({
            ...prev,
            file: ''
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            file: validation.error
          }));
          setSelectedFile(null);
        }
      } else {
        setValidationErrors(prev => ({
          ...prev,
          file: 'يجب رفع القائمة بامتداد CSV'
        }));
        setSelectedFile(null);
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    // Check if fields contain only numbers (based on C# logic)
    const checkNumericOnly = (value, fieldName, errorMessage) => {
      if (value && value.trim()) {
        const trimmedValue = value.trim();
        // Check if the value is purely numeric
        if (!isNaN(parseFloat(trimmedValue)) && isFinite(trimmedValue) && /^\d+(\.\d+)?$/.test(trimmedValue)) {
          errors[fieldName] = errorMessage;
        }
      }
    };

    checkNumericOnly(formData.menuName, 'menuName', 'الاسم لا يجب أن يحتوي على أرقام فقط');
    checkNumericOnly(formData.description, 'description', 'الوصف لا يجب أن يحتوي على أرقام فقط');
    checkNumericOnly(formData.address, 'address', 'العنوان لا يجب أن يحتوي على أرقام فقط');

    // Required field validation
    if (!formData.menuName.trim()) {
      errors.menuName = 'اسم المطعم مطلوب';
    } else if (formData.menuName.trim().length > 50) {
      errors.menuName = 'يجب أن لا يزيد الاسم عن 50 حرف';
    }

    if (!formData.description.trim()) {
      errors.description = 'وصف المطعم مطلوب';
    } else if (formData.description.trim().length > 200) {
      errors.description = 'يجب أن لا يزيد الوصف عن 200 حرف';
    }

    if (!formData.telephone.trim()) {
      errors.telephone = 'رقم التليفون مطلوب';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.telephone.trim())) {
      errors.telephone = 'رقم التليفون غير صحيح';
    }

    if (!formData.address.trim()) {
      errors.address = 'العنوان مطلوب';
    } else if (formData.address.trim().length > 200) {
      errors.address = 'يجب أن لا يزيد العنوان عن 200 حرف';
    }

    if (!formData.deliveryPrice.trim()) {
      errors.deliveryPrice = 'سعر خدمة التوصيل مطلوب';
    } else if (isNaN(parseFloat(formData.deliveryPrice)) || parseFloat(formData.deliveryPrice) < 0) {
      errors.deliveryPrice = 'سعر التوصيل يجب أن يكون رقماً صحيحاً أكبر من أو يساوي صفر';
    }

    if (!formData.vatValue.trim()) {
      errors.vatValue = 'قيمة الضريبة المضافة مطلوبة';
    } else if (isNaN(parseFloat(formData.vatValue)) || parseFloat(formData.vatValue) < 0 || parseFloat(formData.vatValue) > 100) {
      errors.vatValue = 'قيمة الضريبة المضافة يجب أن تكون رقماً بين 0 و 100';
    }

    if (!selectedFile) {
      errors.file = 'يجب رفع ملف القائمة';
    }

    console.log('Validation errors:', errors);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const downloadCSVTemplate = () => {
    // Create CSV template with Arabic headers and sample data
    const csvHeaders = ['السعر', 'النوع','الصنف'];
    const sampleData = [
    ];

    const csvContent = [csvHeaders, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // Create BOM for UTF-8 encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Create and download the file
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'نموذج_قائمة_المطعم.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  };

  const parseCSVFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvContent = e.target.result;
          // Split by lines and filter out empty lines
          const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error('الملف يجب أن يحتوي على بيانات'));
            return;
          }

          // Get headers (first line) - handle different separators and quotes
          const headers = lines[0].split(/[,;]/).map(h => h.trim().replace(/['"]/g, ''));
          
          // Find column indices - be more flexible with column names
          const itemNameIndex = headers.findIndex(h => 
            h === 'الصنف' || h === 'اسم الصنف' || h === 'Item' || h === 'item'
          );
          const priceIndex = headers.findIndex(h => 
            h === 'السعر' || h === 'سعر' || h === 'Price' || h === 'price'
          );
          const categoryIndex = headers.findIndex(h => 
            h === 'النوع' || h === 'نوع' || h === 'Category' || h === 'category' || h === 'Type' || h === 'type'
          );
          
          if (itemNameIndex === -1 || priceIndex === -1 || categoryIndex === -1) {
            reject(new Error('الملف يجب أن يحتوي على الأعمدة: الصنف، السعر، النوع'));
            return;
          }

          // Parse data rows
          const items = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue; // Skip empty lines
            
            // Split by comma or semicolon and clean values
            const values = line.split(/[,;]/).map(v => v.trim().replace(/['"]/g, ''));
            
            if (values.length >= 3) {
              const itemName = values[itemNameIndex]?.trim();
              const priceStr = values[priceIndex]?.trim();
              const categoryName = values[categoryIndex]?.trim();
              
              // Only add if all required fields have values
              if (itemName && priceStr && categoryName) {
                const price = parseFloat(priceStr);
                if (!isNaN(price) && price >= 0) {
                  items.push({
                    Name: itemName,
                    Price: price,
                    CategoryName: categoryName
                  });
                }
              }
            }
          }

          if (items.length === 0) {
            reject(new Error('الملف يجب أن يحتوي على أصناف صحيحة'));
            return;
          }

          resolve(items);
        } catch (error) {
          console.error('CSV parsing error:', error);
          reject(new Error('خطأ في قراءة الملف: ' + error.message));
        }
      };
      reader.readAsText(file, 'UTF-8');
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user || (!user.userId && !user.id)) {
      setValidationErrors({ submit: 'يجب تسجيل الدخول أولاً' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setValidationErrors({});

    try {
      // Parse CSV file to get items
      const items = await parseCSVFile(selectedFile);

      // Prepare the request payload according to your API structure
      const requestPayload = {
        UserId: user?.userId || user?.id, // Include user ID for tracking who created the menu
        MenuData: {
          MenuName: formData.menuName.trim(),
          Description: formData.description.trim(),
          Telephone: formData.telephone.trim(),
          Address: formData.address.trim(),
          VatValue: parseFloat(formData.vatValue) || 0,
          DeliveryPrice: parseFloat(formData.deliveryPrice),
            AddedPercentage: 0
        },
        Items: items
      };

      // Get API URL from environment variables

      console.log('Sending request to:', `${apiUrl}${createMenuEndpoint}`);
      console.log('Request payload:', requestPayload);

      // Make API call to your backend
      const response = await fetch(`${apiUrl}${createMenuEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      // Success case (we already checked response.ok above)
      setSuccessMessage( 'تمت إضافة القائمة بنجاح');
      
      // Reset form
      setFormData({
        menuName: '',
        description: '',
        telephone: '',
        address: '',
        vatValue: '',
        deliveryPrice: '',
      });
      setSelectedFile(null);

    } catch (error) {
      console.error('Error adding menu:', error);
      
      if (error.message.includes('Menu name already exists')) {
        setValidationErrors({submit: error.Message ||'القائمة موجوده بالفعل '  });
      } else if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Failed to fetch')) {
        setValidationErrors({ submit: 'لا يمكن الاتصال بالخادم. تأكد من تشغيل الخادم الخلفي.' });
      } else if (error.message.includes('HTTP 400')) {
        // Extract the actual error message from the response
        const errorMessage = error.message.replace('HTTP 400: ', '');
        setValidationErrors({ submit: errorMessage || 'خطأ في البيانات المرسلة' });
      } else if (error.message.includes('HTTP 500')) {
        setValidationErrors({ submit: 'خطأ في الخادم. يرجى المحاولة مرة أخرى.' });
      } else {
        setValidationErrors({ submit: error.message || 'حدث خطأ أثناء إضافة القائمة' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="main_content">
      <div className="container">
        <h1 className="page-title">إضافة قائمة جديدة</h1>
        
        {successMessage && (
          <div className="alert success">
            {successMessage}
          </div>
        )}

        {validationErrors.submit && (
          <div className="alert error">
            {validationErrors.submit}
          </div>
        )}

        {/* Validation Summary */}
        {(() => {
          const errorEntries = Object.entries(validationErrors).filter(([field, error]) => 
            error && field !== 'submit' && error.trim() !== ''
          );
          console.log('Error entries for summary:', errorEntries);
          
          if (errorEntries.length > 0) {
            return (
              <div className="validation-summary-errors" data-valmsg-summary="true" id="validationSummary">
                <ul>
                  {errorEntries.map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        })()}

        <form onSubmit={handleSubmit} className="form-horizontal">
          <div className="form-group">
            <label htmlFor="menuName">
              <b>اسم المطعم :</b>
            </label>
            <div className="field">
              <input
                type="text"
                id="menuName"
                name="menuName"
                value={formData.menuName}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.menuName ? 'error' : ''}`}
              />
             
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              <b>وصف المطعم :</b>
            </label>
            <div className="field">
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.description ? 'error' : ''}`}
              />
            
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="telephone">
              <b>التليفون :</b>
            </label>
            <div className="field">
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.telephone ? 'error' : ''}`}
              />
            
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">
              <b>العنوان :</b>
            </label>
            <div className="field">
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.address ? 'error' : ''}`}
                rows="3"
              />
             
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="vatValue">
              <b>قيمة الضريبة المضافة (%) :</b>
            </label>
            <div className="field">
              <input
                type="number"
                id="vatValue"
                name="vatValue"
                value={formData.vatValue}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.vatValue ? 'error' : ''}`}
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
              />
             
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="deliveryPrice">
              <b>سعر خدمة التوصيل :</b>
            </label>
            <div className="field">
              <input
                type="number"
                id="deliveryPrice"
                name="deliveryPrice"
                value={formData.deliveryPrice}
                onChange={handleInputChange}
                className={`form-control ${validationErrors.deliveryPrice ? 'error' : ''}`}
                min="0"
                step="1.0"
              />
             
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="menuFile">
              <b>القائمة :</b>
            </label>
            <div className="field">
              <div className="file-upload-container">
                <input
                  type="file"
                  id="menuFile"
                  name="menuFile"
                  onChange={handleFileChange}
                  accept=".csv"
                  className="file-input"
                />
                <div className="file-upload-buttons">
                  <label htmlFor="menuFile" className="file-upload-button">
                    رفع ملف
                  </label>
                </div>
                {selectedFile && (
                  <span className="file-name">{selectedFile.name}</span>
                )}
              </div>
              {validationErrors.file && (
                <span className="error-message">{validationErrors.file}</span>
              )}
              <p className="file-help">يجب أن يكون الملف بصيغة CSV ويحتوي على أعمدة: الصنف، السعر، النوع</p>
            
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ'}
          </button>
            </form>
      </div>
    </section>
  );
};

export default AddRestaurantMenu;
